import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const blogCategories = [
  'AI & Technology',
  'CRM',
  'SEO',
  'Productivity',
  'Marketing',
  'Business',
  'Reviews',
  'Guides',
];

const BlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [validatingAvatar, setValidatingAvatar] = useState(false);
  const [error, setError] = useState('');
  const [avatarError, setAvatarError] = useState('');
  const [avatarValid, setAvatarValid] = useState(true);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image: '',
    category: '',
    author: 'Admin',
    authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
    readTime: '5 min read',
  });

  useEffect(() => {
    if (isEditing) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blogs`);
      const blog = response.data.find(b => b.id === id);
      if (blog) {
        setFormData(blog);
      } else {
        setError('Blog not found');
      }
    } catch (error) {
      setError('Failed to fetch blog');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from title
    if (field === 'title' && !isEditing) {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }

    // Auto-calculate read time from content
    if (field === 'content') {
      const words = value.split(/\s+/).length;
      const minutes = Math.ceil(words / 200);
      setFormData(prev => ({ ...prev, readTime: `${minutes} min read` }));
    }

    // Validate avatar when URL changes
    if (field === 'authorAvatar' && value) {
      validateAvatarImage(value);
    }
  };

  // Avatar size requirements: 100x100 to 200x200 pixels (square)
  const AVATAR_MIN_SIZE = 100;
  const AVATAR_MAX_SIZE = 200;

  const validateAvatarImage = (url) => {
    if (!url) {
      setAvatarError('');
      setAvatarValid(true);
      return;
    }

    setValidatingAvatar(true);
    setAvatarError('');
    
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      
      // Check if square (allow 10% tolerance)
      const aspectRatio = width / height;
      const isSquare = aspectRatio >= 0.9 && aspectRatio <= 1.1;
      
      if (!isSquare) {
        setAvatarError(`Avatar must be square. Current: ${width}x${height}px. Required: square image (1:1 ratio)`);
        setAvatarValid(false);
      } else if (width < AVATAR_MIN_SIZE || height < AVATAR_MIN_SIZE) {
        setAvatarError(`Avatar too small. Current: ${width}x${height}px. Minimum: ${AVATAR_MIN_SIZE}x${AVATAR_MIN_SIZE}px`);
        setAvatarValid(false);
      } else if (width > AVATAR_MAX_SIZE || height > AVATAR_MAX_SIZE) {
        setAvatarError(`Avatar too large. Current: ${width}x${height}px. Maximum: ${AVATAR_MAX_SIZE}x${AVATAR_MAX_SIZE}px`);
        setAvatarValid(false);
      } else {
        setAvatarError('');
        setAvatarValid(true);
      }
      setValidatingAvatar(false);
    };
    
    img.onerror = () => {
      setAvatarError('Failed to load image. Please check the URL.');
      setAvatarValid(false);
      setValidatingAvatar(false);
    };
    
    img.src = url;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, image: response.data.url }));
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate avatar before submission
    if (!avatarValid) {
      setError('Please fix the avatar image before publishing.');
      return;
    }

    if (validatingAvatar) {
      setError('Please wait for avatar validation to complete.');
      return;
    }

    setSaving(true);

    try {
      if (isEditing) {
        await axios.put(`${API}/admin/blogs/${id}`, formData, getAuthHeaders());
      } else {
        await axios.post(`${API}/admin/blogs`, formData, getAuthHeaders());
      }
      navigate('/admin/blogs');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/blogs')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Blog Post' : 'New Blog Post'}
          </h1>
          <p className="text-gray-500">
            {isEditing ? 'Update your article' : 'Write a new blog article'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Article Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter blog title..."
                className="text-lg"
                required
              />
            </div>
            <div>
              <Label>URL Slug *</Label>
              <Input
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="blog-post-url"
                required
              />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {blogCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4">
            <Label>Excerpt *</Label>
            <Textarea
              value={formData.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              placeholder="Brief summary of the article..."
              rows={2}
              required
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Featured Image</h2>
          
          <div className="flex items-start gap-4">
            {formData.image ? (
              <img src={formData.image} alt="Featured" className="w-40 h-24 rounded-lg object-cover" />
            ) : (
              <div className="w-40 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-gray-400" />
              </div>
            )}
            <div className="flex-1">
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload">
                <Button type="button" variant="outline" asChild disabled={uploadingImage}>
                  <span className="cursor-pointer">
                    {uploadingImage ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Upload Image</>
                    )}
                  </span>
                </Button>
              </label>
              <p className="text-xs text-gray-500 mt-2">Or paste an image URL below</p>
              <Input
                value={formData.image}
                onChange={(e) => handleChange('image', e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="mt-2"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Content</h2>
          
          <Textarea
            value={formData.content}
            onChange={(e) => handleChange('content', e.target.value)}
            placeholder="Write your blog content here... You can use basic formatting."
            rows={15}
            className="font-mono text-sm"
            required
          />
          <p className="text-xs text-gray-500 mt-2">
            Estimated read time: {formData.readTime}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Author</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Author Name</Label>
              <Input
                value={formData.author}
                onChange={(e) => handleChange('author', e.target.value)}
                placeholder="Author name"
              />
            </div>
            <div>
              <Label>Author Avatar URL *</Label>
              <div className="flex items-start gap-3">
                {formData.authorAvatar && (
                  <div className="relative">
                    <img 
                      src={formData.authorAvatar} 
                      alt="Avatar preview" 
                      className={`w-12 h-12 rounded-full object-cover border-2 ${
                        avatarValid ? 'border-emerald-500' : 'border-red-500'
                      }`}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    {validatingAvatar && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-full">
                        <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                      </div>
                    )}
                  </div>
                )}
                <div className="flex-1">
                  <Input
                    value={formData.authorAvatar}
                    onChange={(e) => handleChange('authorAvatar', e.target.value)}
                    placeholder="https://example.com/avatar.jpg"
                    className={avatarError ? 'border-red-500' : ''}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Required size: 100x100 to 200x200 pixels (square image)
                  </p>
                  {avatarError && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
                      {avatarError}
                    </p>
                  )}
                  {avatarValid && formData.authorAvatar && !validatingAvatar && !avatarError && (
                    <p className="text-xs text-emerald-600 mt-1">✓ Avatar size is valid</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/blogs')}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-emerald-500 hover:bg-emerald-600" 
            disabled={saving || !avatarValid || validatingAvatar}
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : validatingAvatar ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Validating...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> {isEditing ? 'Update Post' : 'Publish Post'}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;